import React, { useEffect, useMemo, useState } from "react";
import { Pagination } from "flowbite-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  BookOpen,
  Eye,
  Loader2,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
} from "lucide-react";
import AdminLayout, { StatCard } from "./AdminLayout";
import AdminFormModal from "./AdminFormModal";
import { apiGet, apiPost, apiPut, errorMessage, toastOpts } from "./adminApi";
import { getUserRole } from "../../utils/auth";
import {
  AreaBadge,
  ProductPill,
  QualificationCell,
  loadInstitutionNames,
  loadProducts,
  loadStudyAreas,
  paginationTheme,
} from "./qualificationShared";

const PAGE_SIZE = 15;

/**
 * Qualifications (degree programmes) administration: server-side search and
 * pagination, filters by product / study area, create, update and view.
 */
export default function QualificationsPage() {
  const queryClient = useQueryClient();
  const isAdmin = getUserRole() === "Admin";

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [productFilter, setProductFilter] = useState(""); // '' | 'unassigned' | productId
  const [studyAreaFilter, setStudyAreaFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modal, setModal] = useState({ open: false, mode: "create", row: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { data: products = [] } = useQuery({ queryKey: ["adminProducts"], queryFn: loadProducts, staleTime: 5 * 60 * 1000 });
  const { data: studyAreas = [] } = useQuery({ queryKey: ["adminStudyAreas"], queryFn: loadStudyAreas, staleTime: 5 * 60 * 1000 });

  const params = useMemo(() => {
    const p = { page: currentPage - 1, size: PAGE_SIZE };
    if (debouncedSearch) p.search = debouncedSearch;
    if (productFilter === "unassigned") p.unassigned = true;
    else if (productFilter) p.productId = productFilter;
    if (studyAreaFilter) p.studyAreaId = studyAreaFilter;
    return p;
  }, [currentPage, debouncedSearch, productFilter, studyAreaFilter]);

  const { data: page, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["adminQualifications", params],
    queryFn: () => apiGet("hec/qualification/admin/all", params),
    keepPreviousData: true,
    staleTime: 30 * 1000,
    retry: 1,
  });

  const rows = page?.content || [];
  const totalPages = page?.totalPages || 1;
  const totalElements = page?.totalElements || 0;

  // Total (unfiltered) count for the stats card
  const { data: totals } = useQuery({
    queryKey: ["adminQualificationsTotals"],
    queryFn: async () => {
      const [all, unassigned] = await Promise.all([
        apiGet("hec/qualification/admin/all", { page: 0, size: 1 }),
        apiGet("hec/qualification/admin/all", { page: 0, size: 1, unassigned: true }),
      ]);
      return { all: all?.totalElements || 0, unassigned: unassigned?.totalElements || 0 };
    },
    staleTime: 60 * 1000,
  });

  const fields = useMemo(
    () => [
      { name: "qualifactionCode", label: "Qualification Code", type: "text", required: true, placeholder: "Unique code, e.g. UR-CST-001" },
      { name: "qualificationName", label: "Qualification Name", type: "text", required: true, placeholder: "e.g. Bachelor of Science in Computer Science" },
      {
        name: "studyAreaId",
        label: "Study Area",
        type: "select",
        required: true,
        options: studyAreas.map((s) => ({ value: s.id, label: s.studyAreaName })),
      },
      {
        name: "studyArea",
        label: "STEM Classification",
        type: "select",
        placeholder: "Derive from study area",
        options: [
          { value: "STEM", label: "STEM" },
          { value: "NON_STEM", label: "NON STEM" },
        ],
        help: "Leave blank to derive it from the study area name.",
      },
      {
        name: "institution",
        label: "Institution",
        type: "datalist",
        optionsKey: "institutionNames",
        optionsLoader: loadInstitutionNames,
        placeholder: "Institution offering this qualification",
      },
      {
        name: "productId",
        label: "Product",
        type: "select",
        placeholder: "-- No product (unassigned) --",
        options: products.map((p) => ({ value: p.id, label: p.productName })),
      },
      { name: "programDuration", label: "Programme Duration", type: "text", placeholder: "e.g. 4 years" },
      { name: "durationPerYear", label: "Duration per Year", type: "text", placeholder: "e.g. 10 months" },
      { name: "startingYear", label: "Starting Year", type: "text", placeholder: "e.g. 2024" },
      { name: "endingYear", label: "Ending Year", type: "text", placeholder: "e.g. 2028" },
    ],
    [studyAreas, products]
  );

  const initialValues = useMemo(() => {
    const r = modal.row;
    if (!r) return {};
    return {
      ...r,
      studyAreaId: r.studyAreaId?.id || "",
      productId: r.product?.id ?? "",
      studyArea: r.studyArea || "",
    };
  }, [modal.row]);

  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  const handleSubmit = async (values) => {
    const body = {
      ...values,
      productId: values.productId === null || values.productId === "" ? null : Number(values.productId),
      studyArea: values.studyArea || null,
    };
    setIsSubmitting(true);
    try {
      if (modal.mode === "create") {
        await apiPost("hec/qualification/admin/create", body);
        toast.success("Qualification created successfully.", toastOpts);
      } else {
        await apiPut(`hec/qualification/admin/${modal.row.id}`, body);
        toast.success("Qualification updated successfully.", toastOpts);
      }
      closeModal();
      queryClient.invalidateQueries({ queryKey: ["adminQualifications"] });
      queryClient.invalidateQueries({ queryKey: ["adminQualificationsTotals"] });
      queryClient.invalidateQueries({ queryKey: ["productQualifications"] });
    } catch (err) {
      toast.error(errorMessage(err), toastOpts);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClass =
    "px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none bg-white";

  return (
    <AdminLayout
      title="Qualifications"
      subtitle="Degree programmes offered by institutions and the product each one is attached to"
      icon={BookOpen}
      actions={
        <>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {isAdmin && (
            <button
              onClick={() => setModal({ open: true, mode: "create", row: null })}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              New Qualification
            </button>
          )}
        </>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={BookOpen} value={totals?.all ?? "…"} label="Total Qualifications" />
        <StatCard icon={Package} value={totals?.unassigned ?? "…"} label="Without a product" tone="amber" />
        <StatCard icon={Search} value={totalElements} label="Matching current filters" tone="gray" />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className={`${isAdmin ? "text-amber-500" : "text-gray-400"} mb-3`}>
            {isAdmin ? <ShieldCheck className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </div>
          <p className="text-sm font-semibold text-gray-700">{isAdmin ? "Admin Access" : "Read-only Access"}</p>
          <p className="text-xs font-medium mt-1 text-gray-500">
            {isAdmin ? "You can add and update qualifications" : "Only administrators can make changes"}
          </p>
        </div>
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, name or institution..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
            />
          </div>
          <select
            value={productFilter}
            onChange={(e) => {
              setProductFilter(e.target.value);
              setCurrentPage(1);
            }}
            className={`${selectClass} lg:w-64`}
          >
            <option value="">All products</option>
            <option value="unassigned">Unassigned (no product)</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.productName}
              </option>
            ))}
          </select>
          <select
            value={studyAreaFilter}
            onChange={(e) => {
              setStudyAreaFilter(e.target.value);
              setCurrentPage(1);
            }}
            className={`${selectClass} lg:w-56`}
          >
            <option value="">All study areas</option>
            {studyAreas.map((s) => (
              <option key={s.id} value={s.id}>
                {s.studyAreaName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-3" />
            <p className="text-sm">Loading qualifications...</p>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
            <p className="text-sm font-medium text-red-600">Failed to load qualifications</p>
            <p className="text-xs mt-1 text-gray-500">{errorMessage(error)}</p>
            <button onClick={() => refetch()} className="mt-3 text-sm text-cyan-600 hover:underline">
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <BookOpen className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">No qualifications found</p>
            {(debouncedSearch || productFilter || studyAreaFilter) && <p className="text-xs mt-1">Try adjusting your search or filters</p>}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qualification</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Institution</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Study Area</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Product</th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((q, index) => (
                    <tr key={q.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4 text-gray-400 text-xs font-mono">{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                      <td className="px-5 py-4 max-w-md">
                        <QualificationCell q={q} />
                      </td>
                      <td className="px-5 py-4 text-gray-600">{q.institution || <span className="text-gray-400">—</span>}</td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-gray-700">{q.studyAreaId?.studyAreaName || "—"}</span>
                          <AreaBadge area={q.studyArea} />
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">{q.programDuration || <span className="text-gray-400">—</span>}</td>
                      <td className="px-5 py-4">
                        <ProductPill product={q.product} />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setModal({ open: true, mode: "view", row: q })}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => setModal({ open: true, mode: "edit", row: q })}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg hover:bg-cyan-100 transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-gray-100 gap-3">
              <p className="text-sm text-gray-500">
                Showing{" "}
                <span className="font-medium text-gray-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalElements)}
                </span>{" "}
                of <span className="font-medium text-gray-700">{totalElements}</span> qualifications
              </p>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(p) => setCurrentPage(p)}
                  showIcons
                  theme={paginationTheme}
                />
              )}
            </div>
          </>
        )}
      </div>

      <AdminFormModal
        open={modal.open}
        mode={modal.mode}
        icon={BookOpen}
        title={modal.mode === "create" ? "New Qualification" : modal.mode === "edit" ? "Edit Qualification" : "Qualification Details"}
        subtitle={modal.row ? `${modal.row.qualifactionCode} · ${modal.row.qualificationName}` : "Fill in the qualification details"}
        fields={fields}
        initialValues={initialValues}
        onClose={closeModal}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        size="max-w-3xl"
      />
    </AdminLayout>
  );
}
